import { defineStore } from 'pinia';
import { onRequestDefaultError, requests, removeNulls } from '🔗/utils/requests';
// import stores from '🔗/stores';

const usePlaylistsApi = defineStore('playlists-api', {
    state: () => ({
        defaultCover: {
            resized: new URL(`🔗/assets/img/playlists/resized.jpg`, import.meta.url).href,
            original: new URL(`🔗/assets/img/playlists/original.jpg`, import.meta.url).href
        }
    }),
    getters: {
        canView: (state) => (payload) => {
            if (payload.playlist?.state === 'PUBLIC') {
                return true;
            }

            if (stores.user().isPlaylistCurator) {
                if (payload.playlist?.type === 'EDITORIAL') {
                    return true;
                }
            }

            if (payload.playlist?.type === 'USER') {
                return !!payload.playlist?.contributors?.find(item =>
                    item.userId &&
                    stores.user().profile?.id === item.userId
                );
            }
        },

        canAddEditorial: (state) => (payload) => stores.user().isPlaylistCurator,
        canAdd: (state) => (payload) => true,

        canEditCuratorSettings: (state) => (payload) => stores.user().isPlaylistCurator,
        canEditOwnerSettings(state) {
            return (payload) => this.canEdit(payload);
        },

        canEdit: (state) => (payload) => {
            if (stores.user().isPlaylistCurator) {
                if (payload.playlist?.type === 'EDITORIAL') {
                    return true;
                }

                if (payload.playlist?.type === 'USER') {
                    if (payload.playlist?.state === 'PUBLIC') {
                        return true;
                    }
                }
            }

            if (payload.playlist?.type === 'USER') {
                return !!payload.playlist?.contributors?.find(item =>
                        item.userId &&
                        stores.user().profile?.id === item.userId &&
                        (item.role === 'OWNER' || item.role === 'EDITOR')
                    );
            }
        },

        canRemove: (state) => (payload) => {
            if (stores.user().isPlaylistCurator) {
                if (payload.playlist?.type === 'EDITORIAL') {
                    return true;
                }

                if (payload.playlist?.type === 'USER') {
                    if (payload.playlist?.state === 'PUBLIC') {
                        return true;
                    }
                }
            }

            if (payload.playlist?.type === 'USER') {
                return !!payload.playlist?.contributors?.find(item => 
                        item.userId &&
                        stores.user().profile?.id === item.userId &&
                        item.role === 'OWNER'
                    );
            }
        },

        canEditState: (state) => (payload) => {
            if (stores.user().isPlaylistCurator) {
                if (payload.playlist?.type === 'EDITORIAL') {
                    return true;
                }

                if (payload.playlist?.type === 'USER') {
                    if (payload.playlist?.state === 'PUBLIC') {
                        return true;
                    }
                }
            }

            if (payload.playlist?.type === 'USER') {
                return !!payload.playlist?.contributors?.find(item =>
                        item.userId &&
                        stores.user().profile?.id === item.userId &&
                        (item.role === 'OWNER' || item.role === 'EDITOR')
                    );
            }
        },

        canAddTrack: (state) => (payload) => {
            if (stores.user().isPlaylistCurator) {
                if (payload.playlist?.type === 'EDITORIAL') {
                    return true;
                }

                if (payload.playlist?.type === 'USER') {
                    if (payload.playlist?.state === 'PUBLIC') {
                        return true;
                    }
                }
            }

            if (payload.playlist?.type === 'USER') {
                return !!payload.playlist?.contributors?.find(item =>
                        item.userId &&
                        stores.user().profile?.id === item.userId &&
                        (item.role === 'OWNER' || item.role === 'EDITOR')
                    );
            }
        },

        canEditTracks: (state) => (payload) => {
            if (stores.user().isPlaylistCurator) {
                if (payload.playlist?.type === 'EDITORIAL') {
                    return true;
                }

                if (payload.playlist?.type === 'USER') {
                    if (payload.playlist?.state === 'PUBLIC') {
                        return true;
                    }
                }
            }

            if (payload.playlist?.type === 'USER') {
                return !!payload.playlist?.contributors?.find(item =>
                        item.userId &&
                        stores.user().profile?.id === item.userId &&
                        (item.role === 'OWNER' || item.role === 'EDITOR')
                    );
            }
        },

        // contributors: (state) => ({
        //     add: state.addContributor,
        //     remove: state.removeContributor,
        //     list: state.listContributor,
        //     update: state.updateContributor,
        //     changeState: state.changeContributorState
        // }),
        tracks: (state) => ({
            add: state.addTrack,
            remove: state.removeTrack,
            setPosition: state.setTrackPosition,
            list: state.getTracks
        })
    },
    actions: {
        async list(params = {}) {
            try {
                const result = await this.$http({
                    method: 'GET',
                    url: '/playlist/list',
                    params,
                    // signal: requests.signal('playlist-list')
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async get(id) {
            try {
                const result = await this.$http({
                    method: 'GET',
                    url: `/playlist/${ id }`,
                    signal: requests.signal('playlist-get', id)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
       
        async updateCover(id, file) {
            const formData = new FormData();
            formData.append('cover', file);

            try {
                const result = await this.$http({
                    method: 'POST',
                    url: `/playlist/${id}/cover/`,
                    formData,
                    contentType: 'multipart/form-data',
                    signal: requests.signal('playlist-cover', id)
                });

                if ([200, 201].indexOf(result.status) !== -1) {
                    return result.data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },

        async add(data) {
            try {
                const result = await this.$http({
                    method: 'POST',
                    url: '/playlist/create',
                    data: removeNulls(data),
                    // signal: requests.signal('playlist-create')
                });

                if ([200, 201].indexOf(result.status) !== -1) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async update(id, data) {
            try {
                const result = await this.$http({
                    method: 'PUT',
                    url: `/playlist/${ id }`,
                    data,
                    signal: requests.signal('playlist-update', id)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                } else {
                    onRequestDefaultError({ response: result });
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async getAvailableStates(id) {
            try {
                const result = await this.$http({
                    method: 'GET',
                    url: `/playlist/workflow/available/${ id }`,
                    signal: requests.signal('playlist-workflow-available-states', id)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async changeState(id, newState) {
            try {
                const result = await this.$http({
                    method: 'POST',
                    url: `/playlist/workflow/${ id }/${ newState }`,
                    signal: requests.signal('playlist-change-state', id)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                } else {
                    onRequestDefaultError({ response: result });
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async remove(id, with_tracks=false) {
            try {
                const result = await this.$http({
                    method: 'DELETE',
                    url: `/playlist/${ id }`,
                    signal: requests.signal('playlist-remove', id)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },

        // Contributors
        async addContributor(data) {
            // try {
            //     const result = await this.$http({
            //         method: 'POST',
            //         url: '/playlist/contributor/create',
            //         data,
            //         signal: requests.signal('playlist-contributor-create')
            //     });

            //     if (result.status === 200) {
            //         const { data } = result;

            //         return data;
            //     }
            // } catch (e) {
            //     onRequestDefaultError(e);
            // }
        },
        async updateContributor(id, data) {
            // try {
            //     const result = await this.$http({
            //         method: 'PUT',
            //         url: `/playlist/contributor/${ id }`,
            //         data,
            //         signal: requests.signal('playlist-contributor-update', id)
            //     });

            //     if (result.status === 200) {
            //         const { data } = result;

            //         return data;
            //     } else {
            //         onRequestDefaultError({ response: result });
            //     }
            // } catch (e) {
            //     onRequestDefaultError(e);
            // }
        },
        async removeContributor(id) {
            // try {
            //     const result = await this.$http({
            //         method: 'DELETE',
            //         url: `/playlist/contributor/${ id }`,
            //         signal: requests.signal('playlist-contributor-remove', id)
            //     });

            //     if (result.status === 200) {
            //         const { data } = result;

            //         return data;
            //     }
            // } catch (e) {
            //     onRequestDefaultError(e);
            // }
        },
        async changeContributorState(id, newState) {
            // try {
            //     const result = await this.$http({
            //         method: 'POST',
            //         url: `/playlist/contributor/workflow/${ id }/${ newState }`,
            //         signal: requests.signal('playlist-contributor-change-state', id)
            //     });

            //     if (result.status === 200) {
            //         const { data } = result;

            //         return data;
            //     } else {
            //         onRequestDefaultError({ response: result });
            //     }
            // } catch (e) {
            //     onRequestDefaultError(e);
            // }
        },
        async listContributor(params = {}) {
            // try {
            //     const result = await this.$http({
            //         method: 'GET',
            //         url: '/playlist/contributor/list',
            //         params,
            //         // signal: requests.signal('playlist-contributor-list')
            //     });

            //     if (result.status === 200) {
            //         const { data } = result;

            //         return data;
            //     }
            // } catch (e) {
            //     onRequestDefaultError(e);
            // }
        },

        // Tracks
        async addTrack({ playlistId, trackId }) {
            try {
                const result = await this.$http({
                    method: 'POST',
                    url: `/playlist/${playlistId}/track/${trackId}`,
                    signal: requests.signal('playlist-track-add', `${playlistId}-${trackId}`)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                // onRequestDefaultError(e);
            }
        },
        async getTracks(playlistId) {
            try {
                const result = await this.$http({
                    method: 'GET',
                    url: `/playlist/${playlistId}/tracks?sort=position&sort_dir=asc`,
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async setTrackPosition({ playlistId, trackId, position }) {
            try {
                const result = await this.$http({
                    method: 'POST',
                    url: `/playlist/${playlistId}/track/${trackId}/position/${position}`,
                    signal: requests.signal('playlist-track-position', `${playlistId}-${trackId}`)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                } else {
                    onRequestDefaultError({ response: result });
                }
            } catch (e) {
                // onRequestDefaultError(e);
            }
        },
        async removeTrack({ playlistId, trackId }) {
            try {
                const result = await this.$http({
                    method: 'DELETE',
                    url: `/playlist/${playlistId}/track/${trackId}`,
                    signal: requests.signal('playlist-track-remove', `${playlistId}-${trackId}`)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                // onRequestDefaultError(e);
            }
        },
    }
});

export default usePlaylistsApi;
